import { useState, useMemo } from "react";
import { Panel, Label } from "@/components/SplitLayout";
import CopyButton from "@/components/CopyButton";
import { DownloadSimple } from "@phosphor-icons/react";

export default function RobotsTxtGenerator() {
  const [mode, setMode] = useState("allow");
  const [sitemap, setSitemap] = useState("");
  const [crawlDelay, setCrawlDelay] = useState("");
  const [disallow, setDisallow] = useState("/admin\n/cgi-bin");

  const output = useMemo(() => {
    const lines = ["User-agent: *"];
    if (mode === "allow") {
      lines.push("Allow: /");
      disallow
        .split("\n")
        .map((p) => p.trim())
        .filter(Boolean)
        .forEach((p) => lines.push(`Disallow: ${p.startsWith("/") ? p : "/" + p}`));
    } else {
      lines.push("Disallow: /");
    }
    if (crawlDelay && !isNaN(+crawlDelay)) lines.push(`Crawl-delay: ${crawlDelay}`);
    if (sitemap.trim()) {
      lines.push("");
      lines.push(`Sitemap: ${sitemap.trim()}`);
    }
    return lines.join("\n");
  }, [mode, sitemap, crawlDelay, disallow]);

  const download = () => {
    const blob = new Blob([output], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "robots.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-6">
        <Panel title="Configuration">
          <div className="space-y-5">
            <div className="space-y-2">
              <Label>Crawler access</Label>
              <div className="flex gap-2">
                {[
                  { id: "allow", label: "Allow all" },
                  { id: "block", label: "Block all" },
                ].map((o) => (
                  <button
                    key={o.id}
                    data-testid={`robots-mode-${o.id}`}
                    onClick={() => setMode(o.id)}
                    className={`flex-1 border px-4 py-2.5 text-xs font-bold uppercase tracking-[0.1em] transition-all duration-150 ${
                      mode === o.id
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card hover:border-primary hover:text-primary"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {mode === "allow" && (
              <div className="space-y-2">
                <Label>Disallow paths (one per line)</Label>
                <textarea
                  data-testid="robots-disallow"
                  value={disallow}
                  onChange={(e) => setDisallow(e.target.value)}
                  placeholder={"/admin\n/private"}
                  className="h-28 w-full resize-none border border-border bg-card px-4 py-3 font-mono text-sm outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Sitemap URL (optional)</Label>
              <input
                data-testid="robots-sitemap"
                value={sitemap}
                onChange={(e) => setSitemap(e.target.value)}
                placeholder="https://example.com/sitemap.xml"
                className="w-full border border-border bg-card px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="space-y-2">
              <Label>Crawl-delay seconds (optional)</Label>
              <input
                data-testid="robots-crawl-delay"
                value={crawlDelay}
                onChange={(e) => setCrawlDelay(e.target.value)}
                placeholder="10"
                className="w-full border border-border bg-card px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </Panel>
      </div>

      <div className="lg:sticky lg:top-24 lg:self-start">
        <Panel
          title="robots.txt"
          testId="robots-output"
          action={
            <div className="flex gap-2">
              <button
                data-testid="robots-download"
                onClick={download}
                className="inline-flex items-center gap-2 border border-border bg-card px-3.5 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-all duration-150 hover:-translate-y-0.5 hover:border-primary hover:text-primary"
              >
                <DownloadSimple size={15} weight="bold" /> File
              </button>
              <CopyButton text={output} />
            </div>
          }
        >
          <pre className="min-h-[300px] whitespace-pre-wrap bg-background p-4 font-mono text-sm leading-relaxed">
            {output}
          </pre>
        </Panel>
      </div>
    </div>
  );
}
